import { Component, OnInit, computed, input, signal } from '@angular/core';

import { CamAbstractInput } from '@camelot/form-model';
import { TranslatePipe } from '@camelot/translation';
import { ButtonComponent, ButtonSize, SegmentedButtonComponent, SegmentedButtonOption } from '@camelot/ui';

import { FilterDisplayerComponent } from '../../../filters/filter-displayer/filter-displayer.component';
import { SearchDisplayerComponent } from '../../../historical-research/search-displayer.component';
import { GridViewType } from '../../models/types';
import { CamTranslationGrid } from '../../translation.service';
import { AbstractGridComponent } from '../abstract-grid.component';
import { EditabledShortcutsInfoComponent } from '../ag-grid/editabled/shortcuts-info/editabled-shortcuts-info.component';
import { FieldsSelectionDisplayerComponent } from '../fields-selection/fields-selection-displayer/fields-selection-displayer.component';
import { ViewSelectionDisplayerComponent } from '../views-selection/views-selection-displayer/views-selection-displayer.component';

@Component({
  imports: [
    ButtonComponent,
    EditabledShortcutsInfoComponent,
    FieldsSelectionDisplayerComponent,
    FilterDisplayerComponent,
    SearchDisplayerComponent,
    SegmentedButtonComponent,
    TranslatePipe,
    ViewSelectionDisplayerComponent,
  ],
  selector: 'cam-grid-control',
  styleUrl: './grid-control.component.scss',
  templateUrl: './grid-control.component.html',
})
export class GridControlComponent extends AbstractGridComponent<any> implements OnInit {
  show = input<{
    expand?: boolean;
    fields?: boolean;
    filters?: boolean;
    flatView?: boolean;
    search?: boolean;
    searchPlaceholder?: string;
    shortcuts?: boolean;
    showHidden?: boolean;
    switchView?: boolean;
    /** Ajoute la vue tableau custom (`cam-grid-table`) au switch de vue. */
    tableView?: boolean;
    treeMode?: boolean;
    views?: boolean;
  }>({
    filters: false,
    search: true,
    switchView: true,
  });

  size = input<ButtonSize>('sm');

  public readonly filtersForm = signal<CamAbstractInput[]>([]);
  public readonly viewOptions = computed<SegmentedButtonOption<GridViewType>[]>(() => [
    { icon: 'list', value: 'grid' },
    { icon: 'grid', value: 'card' },
    ...(this.show().tableView ? [{ icon: 'table', value: 'table' as const }] : []),
  ]);

  get displayType() {
    return this._grid.displayType;
  }
  get expanded() {
    return this._grid.expanded;
  }
  get filterCount() {
    const model = this._grid.api?.getFilterModel();
    return model ? Object.keys(model).length : 0;
  }
  get isOneExpand() {
    return this._grid.isAtLeastOneNodeOpen;
  }
  get isReady() {
    return this._grid.isReady();
  }
  get isTreeData() {
    return this._grid.isTreeData;
  }
  get showHidden() {
    return this._grid.showHidden;
  }
  get treeMode() {
    return this._grid.treeMode;
  }
  constructor() {
    super();
    CamTranslationGrid.getInstance();
  }

  override ngOnInit() {
    super.ngOnInit();
    if (this._grid.isReady()) {
      this.setFiltersForm();
    } else {
      this._registerSubscription(this._grid.ready$.subscribe(() => this.setFiltersForm()));
    }
    this._registerSubscription(this._grid.filters.filtersChanged$.subscribe(() => this.setFiltersForm()));
    if (this.show().switchView) {
      this._grid.view.enableUserSwitch();
    }
    if (this.breakpoints.isMobile && this.show().switchView) {
      this.switchView('card');
    }
  }

  public collapseAll() {
    this._grid.api?.collapseAll();
  }

  public expandAll() {
    this._grid.api?.expandAll();
  }

  public filtersSelected(data: any) {
    this._grid.filters.applyFiltersForm(data);
    this.setFiltersForm();
  }

  public searchSelected(search: string) {
    this._grid.filters.applySearch(search);
  }

  public setFiltersForm() {
    this.filtersForm.set(this._grid.getFiltersForm());
  }

  public switchView(type: GridViewType, save = true) {
    this._grid.switchView(type, save);
  }

  public toggleExpanded() {
    this._grid.toggleExpanded();
  }

  public toggleFlatView() {
    this._grid.switchTreeMode();
  }

  public toggleShowHidden() {
    this._grid.toggleShowHidden();
  }

  public toggleTreeMode() {
    this._grid.switchTreeMode(!this.treeMode);
  }
}
